import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { useSendEmail, useEmailTemplates, useSignatures } from '../../hooks/useInbox';
import { usePersonalSenders, PROVIDER_GMAIL } from '../../hooks/usePersonalSenders';
import { buildSignatureHtml, hasAuthoredText, withoutSignatureBlocks } from '../../lib/editorSafeHtml';
import { pickSeedSignature } from '../../lib/signatureSelection';
import { useAttachmentUpload } from '../../hooks/useAttachmentUpload';
import { useComposerDraft } from '../../hooks/useComposerDraft';
import MailComposer from './MailComposer';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import ComposeSurface from './ComposeSurface';
import { MAIL_COMPOSE_TOOLBAR } from './editorPresets';
import ProviderBadge from './ProviderBadge';

/**
 * PersonalComposePage — full-page compose for the personal inbox.
 *
 * Replaces the inline compose Dialog modal that was in Inbox.jsx (lines 1421–1591).
 * Uses the same hooks (useSendEmail, useGmailStatus, useOutlookStatus, useEmailTemplates,
 * useSignatures) so the send path is identical to the old modal.
 *
 * Features:
 *  - RichTextEditor (with AI writer, merge variables, content library)
 *  - To / Cc / Bcc via ContactEmailTypeahead
 *  - Template and signature selectors
 *  - Email tracking toggle
 *  - A From picker over the user's connected mailboxes (issue #484). Which
 *    mailbox sends is an explicit choice, not the old Gmail-first precedence.
 *  - localStorage draft auto-save
 *  - Reply prefill via query params (to, subject)
 */
export default function PersonalComposePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user, getAuthHeader } = useAuth();

    // #484 — the sending mailbox, as an explicit selection rather than an
    // inferred precedence. Single source of truth for the picker, the routing
    // hint and the footer.
    const {
        options, provider, selectByOptionValue, selected, selectedAddress, hasAnySender, sendFlags,
        statusUnknown,
        isLoading: sendersLoading,
    } = usePersonalSenders();

    // Templates and signatures
    const { data: templatesData } = useEmailTemplates();
    const { data: signaturesData } = useSignatures();
    const templates = templatesData?.templates || [];
    // #597 — org master templates were never read here, so the Org Templates
    // group could not render at all on this page.
    const adminTemplates = templatesData?.adminTemplates || [];
    const signatures = signaturesData?.signatures || [];
    // #597 — `admin_templates` is the WIRE key; useSignatures's `select`
    // renames it to `adminSignatures`. Reading the wire key off the
    // selected data always yielded undefined, so the Org group never
    // rendered here even when the org had signatures configured.
    const adminSignatures = signaturesData?.adminSignatures || [];
    const defaultSignature = signaturesData?.defaultSignature || null;

    // Send mutation
    const sendEmailMutation = useSendEmail();

    // Form state
    const [to, setTo] = useState([]);
    // With the other hooks rather than beside the handler that uses it:
    // `ComposePage` has early returns above that point and a hook after
    // one is a rules-of-hooks ERROR that fails the build. This file has
    // none today, and should not depend on that staying true.
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [cc, setCc] = useState([]);
    const [bcc, setBcc] = useState([]);
    // Two booleans, not one: the design reveals Cc and Bcc independently.
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [trackEmail, setTrackEmail] = useState(true);
    // #597 — { id, html } of the signature currently in the body, owned here and
    // handed to TemplateSignatureSelectors as a controlled prop. The local
    // templateId/signatureId state and the local applyTemplate/insertSignature
    // are gone: the shared component owns that behaviour now, and its version
    // replaces rather than appends, removes cleanly on None, and preserves the
    // SELECTED signature (not the default) when a template is applied.
    const [signature, setSignature] = useState({ id: '', html: '' });
    // #623 — the SEND path needs these, and it lives here; same ownership rule
    // as the signature.
    const attachmentState = useAttachmentUpload();

    // Draft auto-save key (declared with state for clarity)

    // Resolve signature merge variables
    // #487 — the address this message will actually be sent from. The CRM
    // login email is used ONLY when no mailbox can send, where it genuinely is
    // the sender. A connected mailbox whose address we cannot read merges an
    // EMPTY string rather than the login email: `{{SenderEmail}}` is baked
    // into the body of a message that really goes out, so a wrong address
    // there is worse than a missing one.
    const senderEmailVar = selectedAddress || (hasAnySender ? '' : (user?.email || ''));

    // Prefill from query params (reply / forward)
    useEffect(() => {
        const replyTo = searchParams.get('to');
        // PersonalThreadView.handleForward navigates here with ROUTER STATE
        // ({ subject, body }) rather than URL params, to dodge URL length
        // limits — so reading only searchParams silently dropped the entire
        // forwarded message (#487).
        const prefilledSubject = searchParams.get('subject') || location.state?.subject;
        // #970 — a forwarded message sent from this CRM carries the original
        // sender's signature block; unmark it so it stays quoted content.
        const prefilledBody = withoutSignatureBlocks(searchParams.get('body') || location.state?.body);
        if (replyTo) setTo([{ email: replyTo, label: replyTo }]);
        if (prefilledSubject) setSubject(prefilledSubject);
        if (prefilledBody) setBody(prefilledBody);
    }, [searchParams, location.state]);

    const send = async () => {
        if (statusUnknown) {
            // #487 — see PersonalThreadView.handleSubmitReply.
            toast.error("We couldn't check your mailbox connection. Please retry in a moment.");
            return;
        }
        const toEmail = to.map(c => c.email).filter(Boolean).join(',');
        if (!toEmail) { toast.error('Please fill in recipient'); return; }
        if (!subject.trim()) { toast.error('Subject required'); return; }
        if (!hasAuthoredText(body)) { toast.error('Type a message first'); return; }
        // #623 — never send over an unfinished or failed upload.
        if (attachmentState.pendingCount) { toast.error('Wait for attachments to finish uploading'); return; }
        if (attachmentState.failedCount) { toast.error('Remove or retry the attachments that failed to upload'); return; }
        try {
            const res = await sendEmailMutation.mutateAsync({
                to: toEmail,
                subject,
                body,
                cc: cc.map(c => c.email).filter(Boolean).join(','),
                bcc: bcc.map(c => c.email).filter(Boolean).join(','),
                provider,
                ...sendFlags,
                track: trackEmail && hasAnySender,
                // Staged object keys, not bytes — the server fetches them.
                ...(attachmentState.payload.length && { attachments: attachmentState.payload }),
            });
            // Name the address that ACTUALLY sent — `sentVia` differs from the
            // selection when the chosen mailbox went away mid-compose.
            const usedAddress =
                options.find(o => o.provider === res?.sentVia)?.address || selectedAddress;
            toast.success(
                usedAddress
                    ? `Email sent from ${usedAddress}`
                    : 'Email logged (connect an email provider to send directly)'
            );
            draft.clear();
            navigate('/inbox');
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(
                detail === 'recipient_required' ? 'Please add at least one recipient'
                    : detail === 'send_timeout' ? 'The email server took too long to respond. Please try again.'
                    : detail === 'send_failed' ? 'Failed to send email. The mail server may be busy — please try again in a moment.'
                    : detail || 'Failed to send email'
            );
        }
    };

    /**
     * Draft autosave — #623: the shared `useComposerDraft` hook.
     *
     * The debounce, dirty flag, key shape and beforeunload guard were duplicated
     * byte-for-byte here and in ComposePage; one implementation now, plus the
     * visible "Draft saved at HH:MM" and the Discard confirmation #623 asks for.
     * Drafts stay CLIENT-SIDE — this backend has no draft endpoints, so #623's
     * "ship the endpoint first" does not trigger.
     */
    // `attachmentState.payload` is in here so a draft keeps its files. It was
    // omitted, so every autosave wrote `attachments: []` over whatever the
    // user had attached.
    const draftAttachments = attachmentState.payload;
    const draftValue = useMemo(
        () => ({ to, cc, bcc, subject, body, attachments: draftAttachments }),
        [to, cc, bcc, subject, body, draftAttachments],
    );
    // Reply/forward prefill takes priority over a stored draft.
    const hasPrefill = Boolean(
        searchParams.get('to') || searchParams.get('subject') || searchParams.get('body')
        || location.state?.subject || location.state?.body,
    );
    // Present only when the Drafts view sent us here; autosave then
    // updates THAT draft rather than creating a second copy of it.
    const reopenedDraftId = location.state?.draft?.draft_id || null;
    const draft = useComposerDraft(draftValue, {
        enabled: !hasPrefill, getAuthHeader, inboxId: null, draftId: reopenedDraftId,
    });
    const draftRestored = useRef(false);

useEffect(() => {
        // Compose opens EMPTY. It used to restore whatever was in
        // localStorage, so a new message started with the last one's
        // contents — the reported "compose should always be a new compose
        // state". A draft is now reopened only by clicking it in the Drafts
        // view, which hands it over here in router state: an explicit act
        // rather than a surprise.
        const reopened = location.state?.draft;
        if (!reopened) return;
        if (reopened.to) setTo(reopened.to.map((email) => ({ email })));
        if (reopened.cc) setCc(reopened.cc.map((email) => ({ email })));
        if (reopened.bcc) setBcc(reopened.bcc.map((email) => ({ email })));
        if (reopened.subject) setSubject(reopened.subject);
        if (reopened.body) setBody(reopened.body);
        // The attachments come back with it. They are already in S3 (the
        // draft stored the refs), so they enter `ready` and the send path
        // picks them up unchanged — see `useAttachments.restore`.
        if (reopened.attachments?.length) attachmentState.restore(reopened.attachments);
        // #597/D14 — a reopened draft WINS over the signature seed, and we
        // cannot know which signature (if any) is inside it. An empty tracked
        // value makes the next switch append once rather than strip the wrong
        // thing — the conservative choice when the body's provenance is
        // unknown.
        draftRestored.current = true;
        setSignature({ id: '', html: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * #597 — seed the default (or org-override) signature on open.
     *
     * Declared AFTER the draft-restore effect on purpose: React runs effects in
     * declaration order, so the restore has already run and `draftRestored` is
     * accurate by the time this fires. The previous auto-load effect sat ABOVE
     * the restore and raced it.
     *
     * Gated on `sendersLoading`: `selectedAddress` is undefined until
     * usePersonalSenders settles, and seeding earlier resolves {{SenderEmail}}
     * to an empty string in a message that really goes out (#487). The old
     * auto-load effect had no such gate — a latent race this replaces.
     */
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current || draftRestored.current || sendersLoading) return;
        const seed = pickSeedSignature({
            activeOverrideId: signaturesData?.activeOverrideId,
            signatures: signaturesData?.signatures,
            adminSignatures: signaturesData?.adminSignatures,
            defaultSignature,
        });
        if (!seed) return;
        seeded.current = true;
        // The SAME recipe TemplateSignatureSelectors uses, so its strip matches
        // this seed exactly and the first switch replaces instead of stacking.
        const html = buildSignatureHtml(seed, user, senderEmailVar);
        if (!html) return;
        setSignature({ id: seed.signature_id, html });
        // Never wipe user content — only seed an empty body.
        setBody(prev => (prev ? prev : `<p></p><p></p>${html}`));
    }, [sendersLoading, signaturesData, defaultSignature, user, senderEmailVar]);

    // The footer note IS the provider status — the design centres one line
    // there, and restating it twice is how the two drifted apart before.
    const transport = provider === PROVIDER_GMAIL ? 'Gmail API' : 'Microsoft Graph';
    const note = sendersLoading
        ? 'Checking connected mailboxes…'
        : selectedAddress
            ? `Sending as ${selectedAddress} · via ${transport}`
            : 'No email provider connected — this email will be logged only';
    const noteTestId = sendersLoading
        ? 'senders-loading-indicator'
        : selectedAddress
            ? `${provider}-status-indicator`
            : 'gmail-status-indicator';

    // M-Navigation: prefer history back, fall back to the inbox URL. Split
    // from `leave` so Discard can use the navigation WITHOUT the unsaved-
    // changes prompt — see `discard` below.
    const goBack = () => {
        if (window.history.state && window.history.state.idx > 0) navigate(-1);
        else navigate('/inbox');
    };

    const leave = () => {
        if (draft.isDirty && !window.confirm('You have unsaved changes. Discard and leave?')) return;
        goBack();
    };

    /**
     * Discard: delete the draft, then LEAVE.
     *
     * The callback used to reset the fields and stop there, so the draft went
     * and the user stayed on the compose page staring at an empty composer —
     * which reads as "a new draft has started", the opposite of what they just
     * asked for (#708). `goBack` is the navigation half of `leave`, split out
     * deliberately: calling `leave` here would re-run its own unsaved-changes
     * check against a `draft.isDirty` that has not re-rendered yet, and ask a
     * second time about a draft already deleted.
     *
     * The field reset is kept even though the page unmounts — a stale value
     * restored into the next compose is the failure #694 is about.
     */
    const discard = () => draft.discard(() => {
        setTo([]); setCc([]); setBcc([]); setSubject(''); setBody('');
        setShowCc(false); setShowBcc(false);
        setSignature({ id: '', html: '' });
        attachmentState.reset();
        goBack();
    });

    /** Discard asks first, unless there is nothing to lose. */
    const requestDiscard = () => {
        if (draft.hasDraftContent) setConfirmDiscard(true);
        else discard();
    };

    return (
        <>
        <ComposeSurface
            testId="personal-compose-page"
            onSend={send}
            // See ContactEmailComposer: a send fired before the statuses
            // resolve routes to log-only.
            sending={sendEmailMutation.isPending}
            sendDisabled={sendersLoading}
            hasContent={hasAuthoredText(body)}
            sendLabel={!sendersLoading && !hasAnySender ? 'Log Email' : 'Send'}
            sendTestId="send-email-btn"
            footerSendTestId="personal-compose-send-footer"
            onDiscard={requestDiscard}
            discardTestId="personal-compose-discard-button"
            onClose={leave}
            note={note}
            noteTestId={noteTestId}
            footerExtra={hasAnySender ? (
                <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Track opens and link clicks">
                    <Checkbox
                        checked={trackEmail}
                        onCheckedChange={setTrackEmail}
                        className="w-3.5 h-3.5"
                    />
                    <span className="text-xs text-stone-500 flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        Track
                    </span>
                </label>
            ) : null}
        >
            {/* #623 — the shared composer, in `compose` mode; `page` variant so
                its rows are the design's full-bleed hairlines rather than the
                reply card's stacked fields. The send path, the Track toggle and
                the navigation stay here: they are this lane's, not the
                composer's. */}
            <MailComposer
                mode="compose"
                composerId="personal-compose"
                variant="page"
                // See ComposePage: the reply preset omits merge variables
                // and Content Library, which this page still offers.
                toolbar={MAIL_COMPOSE_TOOLBAR}
                // Preserve this page's long-standing id — the #484/#487
                // regression suites drive the subject through it.
                testIds={{ 'composer-subject': 'compose-subject-input' }}
                // #484 — the From picker's value is a PROVIDER id on this
                // lane, not an address. senderEmail below is the address.
                options={options}
                from={provider}
                onFromChange={selectByOptionValue}
                emptyHint={sendersLoading ? 'Checking connected mailboxes…' : 'No mailbox connected'}
                renderBadge={(opt) => <ProviderBadge provider={opt.provider} />}
                identityNote={selected?.warning === 'no_calendar' ? (
                    <p className="px-5 py-1.5 text-[10px] text-stone-400">
                        Calendar is not connected for this mailbox — sending is unaffected.
                    </p>
                ) : null}
                to={to}
                onToChange={setTo}
                cc={cc}
                onCcChange={setCc}
                bcc={bcc}
                onBccChange={setBcc}
                showCc={showCc}
                onToggleCc={setShowCc}
                showBcc={showBcc}
                onToggleBcc={setShowBcc}
                subject={subject}
                onSubjectChange={setSubject}
                body={body}
                onBodyChange={setBody}
                draftSavedAt={draft.savedAtLabel}
                showMergeVariables
                showAIWriter
                showContentLibrary
                templates={templates}
                adminTemplates={adminTemplates}
                signatures={signatures}
                adminSignatures={adminSignatures}
                activeOverrideId={signaturesData?.activeOverrideId}
                user={user}
                // The ADDRESS, resolved by usePersonalSenders (#487).
                senderEmail={senderEmailVar}
                ownDomain={senderEmailVar}
                signature={signature}
                onSignatureChange={setSignature}
                selectorsDisabled={sendersLoading}
                attachments={attachmentState.attachments}
                onAddAttachments={attachmentState.addAttachments}
                onRemoveAttachment={attachmentState.remove}
            />
        </ComposeSurface>

            <AlertDialog
                open={confirmDiscard}
                onOpenChange={(open) => !open && setConfirmDiscard(false)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
                        <AlertDialogDescription>
                            The draft is deleted and cannot be recovered. Close the
                            composer instead to keep it in Drafts.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep editing</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="compose-discard-confirm"
                            onClick={() => {
                                setConfirmDiscard(false);
                                discard();
                            }}
                        >
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
